import { createApp } from "vue";
import vuetify from "@/setup/vuetify";
import App from "./App.vue";
import SocioManifest from "../socio-manifest.json";
import PackageJson from "../package.json";
import SocioVueComponents, {
  initializeTracingAndLogging,
} from "@socotec.io/socio-vue-components";
import router from "./router";
import { i18n } from "./setup/i18n-setup";
import "@/utils/filters";
import VueSignaturePad from "vue-signature-pad";
import useGlobalComponents from "@/setup/useGlobalComponents";
import SocioQuestionnaire from "./socio-questionnaire/index";
import AosComponents from "@socotec.io/socio-aos-component";
import { useService } from "@/api/connectClient";
import { stonlyIdentifyWidget } from "./api/stonlyIdentifyWidget";
import emitter from "@/setup/mitt";
import { vuetifyProTipTap } from "./setup/tiptap";
import { createPinia } from "pinia";
import { createORM } from "pinia-orm";
import { createOidc } from "vue3-oidc";
import { oidcSettings } from "@/config/OIDCSettings";
import { useNotificationsStore } from "@/store/notifications.js";
import wb from "./registerServiceWorker";
import { WebStorageStateStore } from "oidc-client-ts";
import * as Sentry from "@sentry/vue";
import { getDefaultSentryConfig } from "@socotec.io/socio-grpc-api/configs";
import { plugin as unleashPlugin } from "@unleash/proxy-client-vue";
import posthog from 'posthog-js'

const config = {
  url: import.meta.env.VITE_APP_UNLEASH_URL,
  clientKey: import.meta.env.VITE_APP_UNLEASH_PROXY_SECRETS,
  refreshInterval: 15,
  appName: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
};

const faroSettings = {
  enableTelemetry: import.meta.env.VITE_APP_ENABLE_TELEMETRY,
  grafanaApiKey: import.meta.env.VITE_APP_GRAFANA_API_KEY,
  collectorUrl: import.meta.env.VITE_APP_COLLECTOR_URL,
  propagateUrls: import.meta.env.VITE_APP_PROPAGATE_URLS,
  grpcApiEnv: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
  faroLogLevel: import.meta.env.VITE_APP_FARO_LOG_LEVEL,
};

createOidc({
  oidcSettings: oidcSettings,
  auth: false,
  events: {
    addUserLoaded: (user) => {
      stonlyIdentifyWidget(user.profile.email, user.profile.name);
    },
  },
  refreshToken: {
    enable: true,
    time: 30 * 1000,
  },
  userStore: new WebStorageStateStore({
    prefix: "vue3-oidc",
    store: window.sessionStorage,
  }),
});

const app = createApp(App);

const pinia = createPinia().use(createORM());

app.use(pinia);

const envConfig = {
  i18nLocale: import.meta.env.VITE_APP_I18N_LOCALE,
  i18nFallbackLocale: import.meta.env.VITE_APP_I18N_FALLBACK_LOCALE,
  mapBoxApiKey: import.meta.env.VITE_APP_MAP_BOX,
  clientConnect: useService,
  notificationsStore: useNotificationsStore(),
};
initializeTracingAndLogging(SocioManifest, PackageJson, faroSettings, {
  router,
});
app.use(router);

app.use(AosComponents, { i18n, envConfig });

// Global properties
app.config.globalProperties.$workbox = wb;

// TODO: check if this was ever used, as VSelect is a component of vuetify, this would have been overriden
app.use(VueSignaturePad);
app.use(SocioQuestionnaire);
useGlobalComponents(app);
app.use(SocioVueComponents, i18n);

// Initialize Sentry
Sentry.init({
  ...getDefaultSentryConfig({
    app,
    dsn: "https://02262d0ce1205bfa8402763e0294de31@sentry.tools.infra-socotec.net/5",
    router,
    release: `rapsotec@${import.meta.env.VITE_APP_VERSION}`,
    enabled: import.meta.env.MODE !== "development",
    environment: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
  }),
  debug: import.meta.env.MODE === "development",
  beforeSend(event, _hint) {
    if (import.meta.env.MODE === "development") {
      console.log("Sentry event (dev mode):", {
        message: event.message,
        level: event.level,
        tags: event.tags,
        fingerprint: event.fingerprint,
      });
    }
    return event;
  },
});

const worker = new Worker(
  new URL("./workers/sentry-worker.js", import.meta.url),
  { type: "module" },
);
// Add the integration before listening to worker messages
Sentry.addIntegration(Sentry.webWorkerIntegration({ worker }));

// Handle messages from worker to make worker activity visible in Session Replay
worker.onmessage = (event) => {
  const { type, message, error, payload } = event.data;
  console.log("Worker message received:", event.data);

  switch (type) {
    case "WORKER_READY":
      Sentry.addBreadcrumb({
        category: "worker",
        message: "Sentry worker initialized and ready",
        level: "info",
        timestamp: Date.now() / 1000,
      });
      break;

    case "ERROR_CAPTURED":
      Sentry.addBreadcrumb({
        category: "worker",
        message: `Worker captured error: ${message}`,
        level: "error",
        timestamp: Date.now() / 1000,
      });
      break;

    case "MESSAGE_CAPTURED":
      Sentry.addBreadcrumb({
        category: "worker",
        message: `Worker captured message: ${message}`,
        level: "info",
        timestamp: Date.now() / 1000,
      });
      break;

    case "CAPTURE_FAILED":
      Sentry.addBreadcrumb({
        category: "worker",
        message: `Worker failed to capture: ${error}`,
        level: "error",
        timestamp: Date.now() / 1000,
      });
      break;

    case "WORKER_ERROR":
      Sentry.addBreadcrumb({
        category: "worker",
        message: `Worker initialization error: ${error}`,
        level: "error",
        timestamp: Date.now() / 1000,
      });
      Sentry.captureMessage(`Worker error: ${error}`, "error");
      break;

    case "WORKER_EVENT":
      // Handle generic worker events for session replay visibility
      if (payload?.message) {
        Sentry.addBreadcrumb({
          category: "worker",
          message: payload.message,
          level: "info",
          timestamp: Date.now() / 1000,
        });
      }
      break;

    default:
      // For any other worker events, log them as breadcrumbs
      if (message) {
        Sentry.addBreadcrumb({
          category: "worker",
          message: `Worker event [${type}]: ${message}`,
          level: "info",
          timestamp: Date.now() / 1000,
        });
      }
      break;
  }
};

posthog.init(import.meta.env.VITE_APP_POSTHOG_KEY, {
  api_host: import.meta.env.VITE_APP_POSTHOG_HOST,
  person_profiles: 'identified_only',
  // Pageviews are captured manually in router/index.js (afterEach) to avoid
  // double-counting SPA navigations against posthog-js autocapture.
  capture_pageview: false,
})

const existingErrorHandler = app.config.errorHandler;
app.config.errorHandler = (err, instance, info) => {
  posthog.captureException(err)
  if (existingErrorHandler) existingErrorHandler(err, instance, info)
}


app.use(i18n);
app.use(vuetify);
app.use(vuetifyProTipTap);
app.use(unleashPlugin, { config });
app.config.globalProperties.emitter = emitter;

app.mount("#app");
